<template>
  <div class="image-uploader">
    <el-upload
      class="uploader-box"
      action="#"
      :show-file-list="false"
      :auto-upload="false"
      accept="image/*"
      :on-change="handleChange"
    >
      <div v-if="value" class="preview-wrap">
        <img :src="value" class="preview-img" alt="logo" />
        <div class="preview-mask" :class="{ 'is-uploading': uploading }">
          <i :class="uploading ? 'el-icon-loading' : 'el-icon-refresh'" />
          <span>{{ uploading ? '上传中' : '更换' }}</span>
        </div>
      </div>
      <div v-else class="upload-placeholder">
        <i :class="uploading ? 'el-icon-loading' : 'el-icon-plus'" />
        <span class="upload-tip">{{ uploading ? '上传中…' : tip }}</span>
      </div>
    </el-upload>
    <el-button v-if="value" type="text" class="remove-btn" size="mini" @click="handleRemove">移除</el-button>
    <p v-if="hint" class="uploader-hint">{{ hintText }}</p>
  </div>
</template>

<script>
import uploadToQiniu from '@/util/qiniu-upload'

export default {
  name: 'ImageUploader',
  props: {
    value: { type: String, default: '' },
    // 空态按钮文字（默认沿用 LOGO 场景）
    tip: { type: String, default: '上传 LOGO' },
    // 底部提示；传空字符串则隐藏
    hint: { type: String, default: '支持 PNG / JPG，不超过 {max}MB' },
    // 大小上限（MB）
    maxSize: { type: Number, default: 2 },
    // 预留覆盖：传 raw => Promise<url> 则不走默认七牛直传
    upload: { type: Function, default: null }
  },
  data() {
    return {
      uploading: false
    }
  },
  computed: {
    hintText() {
      return this.hint.replace('{max}', this.maxSize)
    }
  },
  methods: {
    handleChange(file) {
      const raw = file.raw
      if (!raw) return
      if (!/^image\//.test(raw.type)) {
        this.$message.error('只能上传图片文件')
        return
      }
      if (raw.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`图片大小不能超过 ${this.maxSize}MB`)
        return
      }
      if (this.upload) {
        this.uploading = true
        this.upload(raw)
          .then(url => this.emitValue(url))
          .catch(() => {})
          .finally(() => { this.uploading = false })
        return
      }
      this.uploadToQiniu(raw)
    },
    // 默认链路：后端取上传凭证 → 七牛直传 → 回填外链 URL（公共 util）
    uploadToQiniu(raw) {
      this.uploading = true
      uploadToQiniu(raw, 'company').then(url => {
        this.emitValue(url)
      }).catch(() => {
        this.$message.error('图片上传失败，请重试')
      }).finally(() => {
        this.uploading = false
      })
    },
    emitValue(url) {
      this.$emit('input', url)
      this.$emit('change', url)
    },
    handleRemove() {
      this.$emit('input', '')
      this.$emit('change', '')
    }
  }
}
</script>

<style scoped>
.image-uploader {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.uploader-box >>> .el-upload {
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s;
}

.uploader-box >>> .el-upload:hover {
  border-color: var(--primary);
}

.preview-wrap {
  position: relative;
  width: 96px;
  height: 96px;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.preview-wrap:hover .preview-mask {
  opacity: 1;
}

.upload-placeholder {
  width: 96px;
  height: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c939d;
  gap: 6px;
}

.upload-placeholder i {
  font-size: 22px;
}

.upload-tip {
  font-size: 12px;
}

.remove-btn {
  align-self: flex-end;
  color: #f56c6c;
}

.uploader-hint {
  flex: 1 0 100%;
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
